/**
 * Folha do diagrama unifilar no modelo vertical:
 * rede no topo, padrão de entrada, outras cargas, quadro CA,
 * gerador à esquerda e inversor à direita.
 */

import jsPDFImport from 'jspdf'

const jsPDF = typeof jsPDFImport === 'function'
  ? jsPDFImport
  : (jsPDFImport.jsPDF || jsPDFImport.default)

const PRETO = [0, 0, 0]
const VERMELHO = [200, 0, 0]
const AZUL = [0, 0, 170]
const VERDE = [0, 140, 0]

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

const fmt = (n, casas = 2) => (Number(n) || 0).toFixed(casas).replace('.', ',')

const fmtKw = (n) => {
  const v = Number(n) || 0
  const casas = Math.abs(v - Math.round(v)) < 0.05 ? 0 : 1
  return v.toFixed(casas).replace('.', ',')
}

function fmtKwp(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100
  let texto = v.toFixed(2).replace('.', ',')
  if (texto.endsWith(',00')) return texto.slice(0, -3)
  if (texto.endsWith('0')) return texto.slice(0, -1)
  return texto
}

function escrever(pdf, texto, x, y, { size = 8, bold = false, align = 'left', max, cor = PRETO } = {}) {
  const conteudo = String(texto ?? '').trim()
  if (!conteudo) return 0
  pdf.setFont('helvetica', bold ? 'bold' : 'normal')
  pdf.setFontSize(size)
  pdf.setTextColor(cor[0], cor[1], cor[2])
  const linhas = max ? pdf.splitTextToSize(conteudo, max) : [conteudo]
  pdf.text(linhas, x, y, { align, baseline: 'middle', lineHeightFactor: 1.15 })
  return linhas.length * size * 0.3528 * 1.15
}

function traco(pdf, x1, y1, x2, y2, cor = PRETO, largura = 0.35) {
  pdf.setDrawColor(cor[0], cor[1], cor[2])
  pdf.setLineWidth(largura)
  pdf.line(x1, y1, x2, y2)
}

function ponto(pdf, x, y) {
  pdf.setFillColor(0, 0, 0)
  pdf.circle(x, y, 0.65, 'F')
}

function caixaTracejada(pdf, x, y, w, h) {
  pdf.setDrawColor(60, 60, 60)
  pdf.setLineWidth(0.25)
  pdf.setLineDashPattern([1.3, 0.9], 0)
  pdf.rect(x, y, w, h, 'S')
  pdf.setLineDashPattern([], 0)
}

function terra(pdf, x, y) {
  traco(pdf, x, y, x, y + 2.3, VERDE, 0.5)
  traco(pdf, x - 3.4, y + 2.3, x + 3.4, y + 2.3, VERDE, 0.55)
  traco(pdf, x - 2.2, y + 3.9, x + 2.2, y + 3.9, VERDE, 0.55)
  traco(pdf, x - 1.05, y + 5.5, x + 1.05, y + 5.5, VERDE, 0.55)
}

function ligarTerra(pdf, xSpine, y, xFora) {
  traco(pdf, xSpine, y, xFora, y, PRETO, 0.35)
  ponto(pdf, xSpine, y)
  terra(pdf, xFora, y)
}

function setaParaDireita(pdf, xPonta, y) {
  traco(pdf, xPonta - 2.6, y - 1.05, xPonta, y, PRETO, 0.32)
  traco(pdf, xPonta - 2.6, y + 1.05, xPonta, y, PRETO, 0.32)
}

function chamadaCabo(pdf, xFio, y, texto) {
  const conteudo = String(texto || '').trim()
  if (!conteudo) return
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(6.5)
  const largura = pdf.getTextWidth(conteudo)
  const xFim = xFio - 3.1
  const xIni = Math.max(12, xFim - largura)
  escrever(pdf, conteudo, xIni, y - 2.15, { size: 6.5, cor: AZUL })
  traco(pdf, Math.min(xFim, xIni + largura + 0.3), y, xFio, y, PRETO, 0.28)
  setaParaDireita(pdf, xFio, y)
}

function disjuntorNoEixo(pdf, x, y) {
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.35)
  pdf.circle(x, y, 1.35, 'S')
  traco(pdf, x - 2.3, y + 2.1, x + 2.3, y - 2.1, PRETO, 0.35)
}

function simboloInversor(pdf, x, y, w, h) {
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.4)
  pdf.rect(x, y, w, h, 'S')
  traco(pdf, x, y + h, x + w, y, PRETO, 0.35)

  const sx = x + w * 0.1
  const sw = w * 0.34
  const sy = y + h * 0.3
  const amp = Math.min(h * 0.14, 1.7)
  const passos = 14
  let ax = sx
  let ay = sy
  for (let i = 1; i <= passos; i += 1) {
    const t = i / passos
    const bx = sx + sw * t
    const by = sy - Math.sin(t * Math.PI * 2) * amp
    traco(pdf, ax, ay, bx, by, PRETO, 0.32)
    ax = bx
    ay = by
  }

  const x2 = x + w * 0.9
  const x1 = x2 - w * 0.26
  const y0 = y + h * 0.58
  const vao = Math.min(h * 0.12, 1.4)
  for (let i = 0; i < 3; i += 1) {
    traco(pdf, x1, y0 + i * vao, x2, y0 + i * vao, PRETO, 0.35)
  }
}

function nomeDisjuntor(padrao) {
  if (padrao === 'trifasico') return 'Disjuntor Tripolar'
  if (padrao === 'bifasico') return 'Disjuntor Bipolar'
  return 'Disjuntor Monopolar'
}

function tensaoCa(padrao) {
  return padrao === 'trifasico' ? '220/380' : '220'
}

function poloCurto(ligacao) {
  if (ligacao === 'trifasico') return 'Tripolar'
  if (ligacao === 'bifasico') return 'Bipolar'
  return 'Monopolar'
}

function prepararInversores(dados) {
  return inversoresDaFolha(dados).map((inv) => ({
    ...inv,
    ligacao: inv.ligacao || inv.padrao || dados.padrao || 'bifasico'
  }))
}

function textoRamoInversor(inv, indice) {
  const amp = String(inv.disjuntorA || '').trim()
  return `Inv. ${indice + 1} — ${poloCurto(inv.ligacao)}${amp ? ` ${amp} A` : ''}`
}

function inversoresDaFolha(dados) {
  if (Array.isArray(dados.inversores) && dados.inversores.length) return dados.inversores.slice(0, 2)
  return [{
    marca: dados.marcaInversor || '',
    modelo: dados.modeloInversor || '',
    potenciaKw: dados.potenciaInversorKw || '',
    correnteA: dados.correnteInversorA || '',
    disjuntorA: dados.disjuntorA || ''
  }]
}

function listaModulos(dados) {
  const lista = Array.isArray(dados.modulos)
    ? dados.modulos.filter((mod) => mod && (mod.modelo || mod.potenciaW || mod.quantidade))
    : []
  if (lista.length > 1) return lista
  return [{
    modelo: dados.modeloModulo || '',
    potenciaW: dados.potenciaModuloW || '',
    quantidade: dados.qtdModulos || '',
    vmp: dados.vmp || '',
    imp: dados.imp || ''
  }]
}

function modulosDaFolha(dados) {
  return listaModulos(dados).slice(0, 2)
}

function resumoModulos(dados) {
  const mods = listaModulos(dados)
  if (mods.length > 1) {
    let qtd = 0
    let kwp = 0
    mods.forEach((mod) => {
      const n = Math.max(0, parseInt(mod.quantidade, 10) || 0)
      const wp = num(mod.potenciaW)
      qtd += n
      if (n > 0 && wp > 0) kwp += (n * wp) / 1000
    })
    return { qtd, wp: 0, kwp }
  }
  const qtd = Math.max(0, parseInt(dados.qtdModulos, 10) || 0)
  const wp = num(dados.potenciaModuloW)
  const kwp = qtd > 0 && wp > 0 ? (qtd * wp) / 1000 : 0
  return { qtd, wp, kwp }
}

function stringsDaQuantidade(qtd) {
  const n = Math.max(0, parseInt(qtd, 10) || 0)
  if (n <= 14) return 1
  return Math.ceil(n / 12)
}

function textoRamosInversor(inversores) {
  return inversores.map((inv, indice) => textoRamoInversor(inv, indice))
}

function caboInformado(dados, campo, reserva) {
  const texto = String(dados[campo] || '').trim()
  return texto || reserva
}

function reservaCa(padrao) {
  return padrao === 'trifasico' ? '4# (0,6/1 kV)' : '2# (0,6/1 kV)'
}

function encaixarTexto(pdf, itens, xCentro, largura, topo, base, cor = AZUL, ancora = 'centro') {
  const util = base - topo
  const caixa = (size) => size * 0.3528 * 1.15
  const origem = itens.map((item) => (
    typeof item === 'string'
      ? { texto: String(item || '—').toUpperCase(), size: 6.5, bold: false }
      : { texto: String(item.texto || '—'), size: item.size || 6.5, bold: !!item.bold }
  ))
  const montar = (fator) => origem.flatMap((item) => {
    const size = Math.max(1.8, Math.round(item.size * fator * 100) / 100)
    if (item.texto === ' ') return [{ texto: '', size, bold: false }]
    pdf.setFont('helvetica', item.bold ? 'bold' : 'normal')
    pdf.setFontSize(size)
    const partes = pdf.splitTextToSize(item.texto, largura)
    return (partes.length ? partes : ['—']).map((texto) => ({ texto, size, bold: item.bold }))
  })
  const altura = (lista) => lista.reduce((soma, linha) => soma + caixa(linha.size), 0)
  let fator = 1
  let lista = montar(fator)
  while (altura(lista) > util && fator > 0.28) {
    fator = Math.round((fator - 0.03) * 100) / 100
    lista = montar(fator)
  }
  const bloco = altura(lista)
  let cursor = ancora === 'topo'
    ? topo
    : ancora === 'base'
      ? Math.max(topo, base - bloco)
      : topo + Math.max(0, (util - bloco) / 2)
  lista.forEach((linha) => {
    const hLinha = caixa(linha.size)
    escrever(pdf, linha.texto, xCentro, cursor + hLinha / 2, {
      size: linha.size,
      bold: linha.bold,
      align: 'center',
      cor
    })
    cursor += hLinha
  })
}

function desenharCarimbo(pdf, dados, resumo) {
  const x = 10
  const y = 230
  const w = 190
  const h = 56
  const assinaturaW = 74
  const col = (w - assinaturaW) / 3
  const hTitulo = 7
  const hNome = 8
  const hCabeca = 6.5
  const yColunas = y + hTitulo + hNome
  const yConteudo = yColunas + hCabeca

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.5)
  pdf.rect(x, y, w, h, 'S')
  traco(pdf, x, y + hTitulo, x + w, y + hTitulo, PRETO, 0.5)
  traco(pdf, x, yColunas, x + w, yColunas, PRETO, 0.5)
  traco(pdf, x, yConteudo, x + w, yConteudo, PRETO, 0.5)
  for (let i = 1; i <= 3; i += 1) {
    const xv = x + (i < 3 ? col * i : w - assinaturaW)
    traco(pdf, xv, yColunas, xv, y + h, PRETO, 0.5)
  }

  escrever(pdf, 'Projeto Usina Fotovoltaica', x + w / 2, y + hTitulo / 2, { size: 9, align: 'center' })
  encaixarTexto(pdf, [{
    texto: String(dados.cliente || 'CLIENTE').toUpperCase(),
    size: 10,
    bold: true
  }], x + w / 2, w - 8, y + hTitulo + 0.8, yColunas - 0.8)

  const titulos = ['Responsável técnico', 'Capacidade GD', 'Localização da obra', 'Assinatura Digital']
  const centros = [col / 2, col + col / 2, col * 2 + col / 2, w - assinaturaW / 2]
  titulos.forEach((titulo, i) => {
    escrever(pdf, titulo, x + centros[i], yColunas + hCabeca / 2, {
      size: 7.5,
      bold: true,
      align: 'center',
      cor: VERMELHO,
      max: (i === 3 ? assinaturaW : col) - 3
    })
  })

  const larguraColuna = col - 5
  const desenharNoBloco = (itens, xCentro, largura) => {
    encaixarTexto(pdf, itens, xCentro, largura, yConteudo + 1.4, y + h - 1.4)
  }
  desenharNoBloco([
    'Wallafe Sousa Barros',
    'Téc em Eletrotécnica',
    'CFT: 91895022215'
  ], x + centros[0], larguraColuna)

  const kwp = resumo.kwp > 0 ? `${fmtKwp(resumo.kwp)} kWp` : '—'
  desenharNoBloco([
    { texto: kwp, size: 11, bold: true },
    { texto: 'Data', size: 8, bold: true },
    { texto: new Date().toLocaleDateString('pt-BR'), size: 8, bold: false }
  ], x + centros[1], larguraColuna)

  desenharNoBloco(linhasLocalizacao(dados), x + centros[2], larguraColuna)
}

function formatarCep(cep) {
  const digitos = String(cep || '').replace(/\D/g, '').slice(0, 8)
  if (!digitos) return ''
  if (digitos.length <= 5) return digitos
  return `${digitos.slice(0, 5)}-${digitos.slice(5)}`
}

function linhasLocalizacao(dados) {
  const partes = String(dados.endereco || '').split(',').map((s) => s.trim()).filter(Boolean)
  const cidade = String(dados.cidade || '').trim()
  const uf = String(dados.uf || '').trim()
  const cidadeUf = [cidade, uf].filter(Boolean).join(' - ')
  const cep = formatarCep(dados.cep)
  const linhas = []
  if (partes.length >= 3) linhas.push(partes.slice(0, -1).join(', '), partes[partes.length - 1])
  else if (partes.length === 2) linhas.push(partes[0], partes[1])
  else linhas.push(partes[0] || '—')
  if (cep) linhas.push(`CEP ${cep}`)
  linhas.push(cidadeUf || '—')
  return linhas
}

function textoCaboGerador(dados) {
  const mods = modulosDaFolha(dados)
  const strings = mods.length > 1
    ? mods.reduce((soma, mod) => soma + stringsDaQuantidade(mod.quantidade), 0)
    : Math.max(1, parseInt(dados.qtdStrings, 10) || 1)
  const condutores = Math.max(1, strings) * 2
  return `${condutores}#4 mm² (1,8 kVcc)`
}

function linhasDoModulo(mod) {
  const qtd = Math.max(0, parseInt(mod.quantidade, 10) || 0)
  const wp = num(mod.potenciaW)
  const kwp = qtd > 0 && wp > 0 ? (qtd * wp) / 1000 : 0
  const linhas = []
  if (wp > 0) linhas.push(`Pn = ${fmt(wp, 0)} W.`)
  if (String(mod.vmp || '').trim()) linhas.push(`Vmp = ${String(mod.vmp).trim()} V.`)
  if (String(mod.imp || '').trim()) linhas.push(`Imp = ${String(mod.imp).trim()} A.`)
  linhas.push(`Quantidade = ${qtd || '—'}.`)
  linhas.push(`Total = ${kwp > 0 ? `${fmtKwp(kwp)} kWp.` : '—'}`)
  return linhas
}

function desenharCaixaGerador(pdf, x, y, w, h) {
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.6)
  pdf.rect(x, y, w, h, 'S')
  escrever(pdf, 'G', x + w / 2, y + h / 2, { size: 16, bold: true, align: 'center' })
}

function desenharTextoGerador(pdf, linhas, xCentro, yPrimeira, passo) {
  linhas.forEach((linha, i) => {
    escrever(pdf, linha, xCentro, yPrimeira + i * passo, { size: 7.2, align: 'center' })
  })
}

function terraAoLado(pdf, xCaixa, yMeio) {
  const xHaste = xCaixa - 4.6
  traco(pdf, xHaste, yMeio, xCaixa, yMeio)
  terra(pdf, xHaste, yMeio)
}

function desenharGerador(pdf, dados, resumo, xSpine, yLigacao, ligar = true) {
  const xg = 16
  const yg = yLigacao - 9
  const centro = xg + 11
  const linhas = []
  if (resumo.wp > 0) linhas.push(`Pn = ${fmt(resumo.wp, 0)} W.`)
  if (String(dados.vmp || '').trim()) linhas.push(`Vmp = ${String(dados.vmp).trim()} V.`)
  if (String(dados.imp || '').trim()) linhas.push(`Imp = ${String(dados.imp).trim()} A.`)
  linhas.push(`Quantidade = ${resumo.qtd || '—'}.`)
  linhas.push(`Total = ${resumo.kwp > 0 ? `${fmtKwp(resumo.kwp)} kWp.` : '—'}`)
  const passo = 3.6
  let cursor = yg - 2.4 - (linhas.length - 1) * passo
  linhas.forEach((linha) => {
    escrever(pdf, linha, centro, cursor, { size: 7.5, align: 'center' })
    cursor += passo
  })

  desenharCaixaGerador(pdf, xg, yg, 22, 18)
  terra(pdf, centro, yg + 18)
  if (!ligar) return
  traco(pdf, xg + 22, yLigacao, xSpine, yLigacao)
  setaParaDireita(pdf, xSpine, yLigacao)
  escrever(pdf, textoCaboGerador(dados), (xg + 22 + xSpine) / 2, yLigacao - 2.3, { size: 6.5, align: 'center', cor: AZUL, max: 50 })
}

function desenharGeradoresEmpilhados(pdf, dados, xSpine, yBus) {
  const mods = modulosDaFolha(dados)
  const w = 22
  const h = 15
  const xg = 18
  const centro = xg + w / 2
  const passo = 3.15
  const vao = 5
  const yInf = yBus - h / 2
  const ySup = yInf - vao - h
  const linhasSup = linhasDoModulo(mods[0] || {})
  const linhasInf = linhasDoModulo(mods[1] || {})

  desenharTextoGerador(pdf, linhasSup, centro, ySup - 2.2 - (linhasSup.length - 1) * passo, passo)
  desenharCaixaGerador(pdf, xg, ySup, w, h)
  desenharCaixaGerador(pdf, xg, yInf, w, h)
  traco(pdf, centro, ySup + h, centro, yInf)
  desenharTextoGerador(pdf, linhasInf, centro, yInf + h + 3.2, passo)
  terraAoLado(pdf, xg, ySup + h / 2)
  terraAoLado(pdf, xg, yInf + h / 2)

  traco(pdf, xg + w, yBus, xSpine, yBus)
  ponto(pdf, xSpine, yBus)
  escrever(pdf, textoCaboGerador(dados), (xg + w + xSpine) / 2, yBus - 2.4, {
    size: 6.5,
    align: 'center',
    cor: AZUL,
    max: xSpine - xg - w - 4
  })
}

function linhasInversorDesenho(inv) {
  const kw = num(inv.potenciaKw)
  const modelo = String(inv.modelo || '').trim()
  return [
    { texto: kw > 0 ? `INVERSOR ${fmtKw(kw)} kW` : 'INVERSOR', size: 6.4, bold: true },
    { texto: modelo || 'Modelo a informar', size: 6.2, bold: false },
    { texto: `V = ${tensaoCa(inv.ligacao)}`, size: 6.2, bold: false },
    { texto: inv.correnteA ? `In = ${inv.correnteA} A` : 'In a informar', size: 6.2, bold: false },
    { texto: '>> ANTI-ILHAMENTO', size: 5.5, bold: false },
    { texto: '>> AFCI', size: 5.5, bold: false },
    { texto: 'Qtd = 1', size: 6.2, bold: false }
  ]
}

function desenharTextoAcima(pdf, linhas, xCentro, yBase, larguraMax) {
  let fator = 1
  const medir = (f) => {
    let max = 0
    linhas.forEach((linha) => {
      pdf.setFont('helvetica', linha.bold ? 'bold' : 'normal')
      pdf.setFontSize(Math.max(4.2, linha.size * f))
      max = Math.max(max, pdf.getTextWidth(linha.texto))
    })
    return max
  }
  while (medir(fator) > larguraMax && fator > 0.72) fator = Math.round((fator - 0.04) * 100) / 100
  const passo = 2.4 * fator
  const yTopo = yBase - (linhas.length - 1) * passo
  linhas.forEach((linha, i) => {
    escrever(pdf, linha.texto, xCentro, yTopo + i * passo, {
      size: Math.max(4.2, linha.size * fator),
      bold: linha.bold,
      align: 'center'
    })
  })
}

function desenharInversorAoLado(pdf, inv, xSpine, yBus) {
  const wSim = 22
  const hSim = 12
  const xSim = xSpine + 4
  const ySim = yBus - hSim / 2
  traco(pdf, xSpine, yBus, xSim, yBus)
  simboloInversor(pdf, xSim, ySim, wSim, hSim)
  desenharUmInversor(pdf, inv, xSim + wSim + 1.6, yBus - 9.15, 60, false)
  const cx = xSim + wSim / 2
  traco(pdf, cx, ySim + hSim, cx, ySim + hSim + 2)
  terra(pdf, cx, ySim + hSim + 2)
}

function desenharInversoresLadoALado(pdf, inversores, xSpine, yBus) {
  const w = 22
  const h = 11
  const gap = 5
  const x0 = xSpine + 6
  const y = yBus - h / 2
  inversores.forEach((inv, i) => {
    const x = x0 + i * (w + gap)
    traco(pdf, i === 0 ? xSpine : x - gap, yBus, x, yBus)
    simboloInversor(pdf, x, y, w, h)
    desenharTextoAcima(pdf, linhasInversorDesenho(inv), x + w / 2, y - 1.5, w + gap - 1.2)
    const cx = x + w / 2
    traco(pdf, cx, y + h, cx, y + h + 1.5)
    terra(pdf, cx, y + h + 1.5)
  })
  ponto(pdf, xSpine, yBus)
}

function desenharUmInversor(pdf, inv, x, y, larguraMax, compacto) {
  const passo = compacto ? 2.7 : 3.05
  const kw = num(inv.potenciaKw)
  const modelo = String(inv.modelo || '').trim()
  const linhas = [
    { texto: kw > 0 ? `INVERSOR ${fmtKw(kw)} kW` : 'INVERSOR', size: compacto ? 7 : 8, bold: true },
    { texto: modelo || 'Modelo a informar', size: 7, bold: false },
    { texto: `V = ${tensaoCa(inv.ligacao)}`, size: 7, bold: false },
    { texto: inv.correnteA ? `In = ${inv.correnteA} A` : 'In a informar', size: 7, bold: false },
    { texto: '>> ANTI-ILHAMENTO', size: 6.5, bold: false },
    { texto: '>> AFCI', size: 6.5, bold: false },
    { texto: 'Qtd = 1', size: 7, bold: false }
  ]
  const largura = Math.min(larguraMax, Math.max(...linhas.map((linha) => {
    pdf.setFont('helvetica', linha.bold ? 'bold' : 'normal')
    pdf.setFontSize(linha.size)
    return pdf.getTextWidth(linha.texto)
  })))
  const xc = x + largura / 2
  linhas.forEach((linha, i) => {
    escrever(pdf, linha.texto, xc, y + i * passo, {
      size: linha.size,
      bold: linha.bold,
      align: 'center',
      max: larguraMax
    })
  })
}

export function montarFolhaUnifilar(dados = {}) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const resumo = resumoModulos(dados)
  const inversores = prepararInversores(dados)
  const x = 112
  const nomePolo = nomeDisjuntor(dados.padrao)
  const ampPadrao = String(dados.disjuntorPadrao || dados.disjuntorA || inversores[0]?.disjuntorA || '').trim()
  const caboCa = caboInformado(dados, 'caboCa', reservaCa(dados.padrao))
  const caboRamal = caboInformado(dados, 'caboRamal', reservaCa(dados.padrao))
  const caboQuadro = caboInformado(dados, 'caboQuadro', caboCa)

  const boxX = 46
  const boxW = 126
  const boxR = boxX + boxW
  const xTerra = 188
  const textoDisjuntor = (amp) => `${nomePolo} ${amp ? `${amp} A` : ''} / Vca`.replace('  ', ' ').trim()

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.7)
  pdf.rect(8, 8, 194, 281, 'S')

  escrever(pdf, 'REDE DE BAIXA TENSÃO', 105, 15, { size: 9, bold: true, align: 'center', cor: VERMELHO })
  traco(pdf, 48, 20, 176, 20, PRETO, 0.45)
  ponto(pdf, x, 20)

  caixaTracejada(pdf, boxX, 23, boxW, 52)
  escrever(pdf, 'PADRÃO DE ENTRADA', boxR - 3, 28, { size: 8, bold: true, align: 'right', cor: VERMELHO })

  const yNome = 33
  const xNome = 48
  const wNome = 42
  const hNome = 8
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.35)
  pdf.rect(xNome, yNome - hNome / 2, wNome, hNome, 'S')
  encaixarTexto(
    pdf,
    [{ texto: String(dados.cliente || 'CLIENTE').toUpperCase(), size: 5.5, bold: true }],
    xNome + wNome / 2,
    wNome - 2.2,
    yNome - hNome / 2 + 0.5,
    yNome + hNome / 2 - 0.5,
    PRETO
  )

  const yMedidor = 52
  const ySetaRamal = 44
  const xSeta = 58
  escrever(pdf, caboRamal, (xSeta + x - 3) / 2, ySetaRamal - 2.5, { size: 6.2, align: 'center', cor: AZUL, max: x - xSeta - 4 })
  traco(pdf, x, 20, x, yMedidor - 4)
  traco(pdf, xSeta, ySetaRamal, x, ySetaRamal)
  setaParaDireita(pdf, x, ySetaRamal)

  pdf.setLineWidth(0.4)
  pdf.circle(x, yMedidor, 4, 'S')
  escrever(pdf, 'M', x, yMedidor, { size: 8, bold: true, align: 'center' })
  escrever(pdf, 'MEDIDOR', x + 8, yMedidor, { size: 8, bold: true })
  traco(pdf, x, yMedidor + 4, x, 60)
  disjuntorNoEixo(pdf, x, 63)
  traco(pdf, x, 67, x, 75)
  escrever(pdf, textoDisjuntor(ampPadrao), x + 6, 63, { size: 7.5, max: boxR - x - 8 })
  ligarTerra(pdf, x, 71, xTerra)

  traco(pdf, x, 75, x, 88)
  chamadaCabo(pdf, x, 81.5, caboCa)

  caixaTracejada(pdf, boxX, 88, boxW, 34)
  traco(pdf, x, 88, x, 94)
  disjuntorNoEixo(pdf, x, 98)
  traco(pdf, x, 102, x, 122)
  escrever(pdf, textoDisjuntor(ampPadrao), x + 6, 98, { size: 7.5, max: boxR - x - 8 })

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.35)
  pdf.rect(58, 106, 36, 12, 'S')
  escrever(pdf, 'Outras cargas', 76, 112, { size: 7.5, align: 'center' })
  traco(pdf, 76, 118, 76, 120)
  traco(pdf, 76, 120, x, 120)
  ponto(pdf, x, 120)
  traco(pdf, 74, 118, 76, 120)
  traco(pdf, 78, 118, 76, 120)
  ligarTerra(pdf, x, 112, xTerra)

  traco(pdf, x, 122, x, 136)
  chamadaCabo(pdf, x, 129, caboQuadro)

  caixaTracejada(pdf, boxX, 136, boxW, 36)
  escrever(pdf, 'Q. PROTEÇÃO CA', boxR - 3, 141, { size: 8, bold: true, align: 'right', cor: VERMELHO })
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.3)
  pdf.rect(52, 146, 28, 16, 'S')
  const qtdDps = dados.padrao === 'trifasico' ? '3' : '2'
  escrever(pdf, `${qtdDps} DPS CA`, 66, 151, { size: 6.5, align: 'center' })
  escrever(pdf, 'Classe II', 66, 156, { size: 6.5, align: 'center' })
  const variosInv = inversores.length > 1
  const variosMod = modulosDaFolha(dados).length > 1
  const yBus = variosInv ? 203 : 200
  const maxTexto = boxR - x - 8

  if (variosInv) {
    traco(pdf, 80, 146, x, 146)
    ponto(pdf, x, 146)
    traco(pdf, x, 136, x, 147)
    disjuntorNoEixo(pdf, x, 151)
    traco(pdf, x, 155, x, 158)
    disjuntorNoEixo(pdf, x, 162)
    traco(pdf, x, 166, x, 172)
    const geral = String(dados.disjuntorA || '').trim()
    escrever(pdf, `${nomePolo} (GERAL)`, x + 6, 147.2, { size: 6.5, max: maxTexto })
    escrever(pdf, geral ? `${geral} A / Vca` : 'A / Vca', x + 6, 151.6, { size: 6.5, max: maxTexto })
    textoRamosInversor(inversores).forEach((linha, indice) => {
      escrever(pdf, linha, x + 6, 156.6 + indice * 4.2, { size: 6.3, max: maxTexto })
    })
    ligarTerra(pdf, x, 168, xTerra)
  } else {
    traco(pdf, 80, 154, x, 154)
    ponto(pdf, x, 154)
    traco(pdf, x, 136, x, 158)
    disjuntorNoEixo(pdf, x, 162)
    traco(pdf, x, 166, x, 172)
    const ampInv = String(inversores[0]?.disjuntorA || dados.disjuntorA || '').trim()
    const textoQuadro = `${nomeDisjuntor(inversores[0]?.ligacao || dados.padrao)} ${ampInv ? `${ampInv} A` : ''} / Vca`.replace('  ', ' ').trim()
    escrever(pdf, textoQuadro, x + 6, 162, { size: 7.5, max: maxTexto })
    ligarTerra(pdf, x, 166, xTerra)
  }

  traco(pdf, x, 172, x, yBus)
  if (!variosInv && !variosMod) {
    desenharGerador(pdf, dados, resumo, x, yBus)
    desenharInversorAoLado(pdf, inversores[0], x, yBus)
  } else {
    if (variosMod) {
      desenharGeradoresEmpilhados(pdf, dados, x, yBus)
    } else {
      desenharGerador(pdf, dados, resumo, x, yBus, false)
      traco(pdf, 38, yBus, x, yBus)
      escrever(pdf, textoCaboGerador(dados), (38 + x) / 2, yBus - 2.4, {
        size: 6.5,
        align: 'center',
        cor: AZUL,
        max: x - 42
      })
    }
    if (variosInv) desenharInversoresLadoALado(pdf, inversores, x, yBus)
    else desenharInversorAoLado(pdf, inversores[0], x, yBus)
  }

  desenharCarimbo(pdf, dados, resumo)
  return pdf
}

function setaParaBaixo(pdf, x, yPonta) {
  traco(pdf, x, yPonta - 6.5, x, yPonta, VERMELHO, 0.4)
  traco(pdf, x - 1.15, yPonta - 1.8, x, yPonta, VERMELHO, 0.4)
  traco(pdf, x + 1.15, yPonta - 1.8, x, yPonta, VERMELHO, 0.4)
}

function simboloString(pdf, x, y, w, h) {
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.45)
  pdf.rect(x, y, w, h, 'S')
  traco(pdf, x, y, x + w / 2, y + h * 0.46, PRETO, 0.35)
  traco(pdf, x + w, y, x + w / 2, y + h * 0.46, PRETO, 0.35)
  escrever(pdf, 'String cc', x + w / 2, y + h * 0.72, { size: w < 20 ? 5.2 : 6.2, align: 'center' })
}

function itensDoModulo(mod) {
  return linhasDoModulo(mod).map((texto) => ({ texto, size: 6.6 }))
}

function linhasGeracaoBlocos(dados) {
  return itensDoModulo(listaModulos(dados)[0] || {})
}

function itensProtecao(dados, inversores) {
  const amp = String(dados.disjuntorA || '').trim()
    || String((inversores[0] && inversores[0].disjuntorA) || '').trim()
  if (inversores.length < 2) {
    const ligacao = (inversores[0] && inversores[0].ligacao) || dados.padrao
    return [
      { texto: nomeDisjuntor(ligacao).toUpperCase(), size: 6.3, bold: true },
      { texto: 'TERMOMAGNÉTICO', size: 6.1, bold: true },
      { texto: amp ? `${amp} A CURVA C` : 'CURVA C', size: 6.1, bold: true }
    ]
  }
  return [
    { texto: nomeDisjuntor(dados.padrao).toUpperCase(), size: 6, bold: true },
    { texto: 'TERMOMAGNÉTICO', size: 5.8, bold: true },
    { texto: amp ? `GERAL ${amp} A CURVA C` : 'GERAL', size: 5.8, bold: true },
    ...inversores.map((inv, indice) => ({
      texto: `INV. ${indice + 1} ${poloCurto(inv.ligacao).toUpperCase()} ${String(inv.disjuntorA || '').trim() || '—'} A`,
      size: 5.6
    }))
  ]
}

function desenharDiagramaReplicado(pdf, dados, mods, inversores, resumo) {
  const w = 18
  const h = 18
  const vao = 14
  const passo = 40
  const larguraTexto = 36

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(6.2)
  const larguraRede = Math.max(
    pdf.getTextWidth('Rede'),
    pdf.getTextWidth('de'),
    pdf.getTextWidth('Distribuição')
  )
  const dxRede = passo * 2 + 34 + 18
  const esquerda = larguraTexto / 2
  const direita = dxRede + 4 + larguraRede
  const xGer = 105 - (direita - esquerda) / 2
  const xInv = xGer + passo
  const xCa = xInv + passo
  const xMed = xCa + 34
  const xRede = xMed + 18

  const gerDuplo = mods.length > 1
  const invDuplo = inversores.length > 1
  const linhasAcima = Math.max(
    gerDuplo ? itensDoModulo(mods[0]).length : 0,
    invDuplo ? linhasInversorDesenho(inversores[0]).length : 0
  )
  const reservaAcima = Math.max(16, linhasAcima * 3.05)
  const sobe = vao / 2 + h + 8 + reservaAcima + 5
  const desce = vao / 2 + h + 8 + 7 * 3.05
  const yBus = (16 + 224) / 2 + (sobe - desce) / 2

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.7)
  pdf.rect(8, 8, 194, 281, 'S')

  const borda = (x, duplo) => (duplo
    ? { entra: x, sai: x }
    : { entra: x - w / 2, sai: x + w / 2 })
  const ger = borda(xGer, gerDuplo)
  const inv = borda(xInv, invDuplo)
  traco(pdf, ger.sai, yBus, inv.entra, yBus, PRETO, 0.45)
  traco(pdf, inv.sai, yBus, xCa - w / 2, yBus, PRETO, 0.45)
  traco(pdf, xCa + w / 2, yBus, xMed - 8, yBus, PRETO, 0.45)
  traco(pdf, xMed + 8, yBus, xRede, yBus, PRETO, 0.45)
  traco(pdf, xRede, yBus - 12, xRede, yBus + 12, PRETO, 0.75)
  if (gerDuplo) ponto(pdf, xGer, yBus)
  if (invDuplo) ponto(pdf, xInv, yBus)

  const desenharPilha = (x, duplo, desenhar, itens, titulo) => {
    if (duplo) {
      const yTop = yBus - vao / 2 - h
      const yBot = yBus + vao / 2
      traco(pdf, x, yTop + h, x, yBot, PRETO, 0.45)
      desenhar(x - w / 2, yTop)
      desenhar(x - w / 2, yBot)
      const ySeta = yTop - 0.5
      setaParaBaixo(pdf, x, ySeta)
      const yBaseTexto = ySeta - 6.6
      const yTopoTexto = yBaseTexto - reservaAcima
      encaixarTexto(pdf, itens[0], x, larguraTexto, yTopoTexto, yBaseTexto, PRETO, 'topo')
      escrever(pdf, titulo, x, yTopoTexto - 3.4, { size: 8, align: 'center' })
      const yTexto = yBot + h + 8.2
      setaParaBaixo(pdf, x, yTexto - 0.6)
      encaixarTexto(pdf, itens[1], x, larguraTexto, yTexto, 220, PRETO, 'topo')
      return
    }
    const y = yBus - h / 2
    desenhar(x - w / 2, y)
    escrever(pdf, titulo, x, y - 6.2, { size: 8, align: 'center' })
    const yTexto = y + h + 8.2
    setaParaBaixo(pdf, x, yTexto - 0.6)
    encaixarTexto(pdf, itens[0], x, larguraTexto, yTexto, 220, PRETO, 'topo')
  }

  desenharPilha(
    xGer,
    gerDuplo,
    (sx, sy) => simboloString(pdf, sx, sy, w, h),
    mods.map((mod) => itensDoModulo(mod)),
    'Geração'
  )
  desenharPilha(
    xInv,
    invDuplo,
    (sx, sy) => simboloInversor(pdf, sx, sy, w, h),
    inversores.map((inv) => linhasInversorDesenho(inv)),
    'Inversores'
  )

  const yCa = yBus - h / 2
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.45)
  pdf.rect(xCa - w / 2, yCa, w, h, 'S')
  escrever(pdf, 'CA', xCa, yBus, { size: 12, bold: true, align: 'center' })
  escrever(pdf, 'Proteção CA', xCa, yCa - 6.2, { size: 8, align: 'center' })
  const yProt = yCa + h + 8.2
  setaParaBaixo(pdf, xCa, yProt - 0.6)
  encaixarTexto(pdf, itensProtecao(dados, inversores), xCa, 42, yProt, 220, PRETO, 'topo')

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.45)
  pdf.circle(xMed, yBus, 8, 'S')
  escrever(pdf, 'M', xMed, yBus, { size: 12, bold: true, align: 'center' })
  escrever(pdf, 'Medição', xMed, yBus - h / 2 - 6.2, { size: 8, align: 'center' })
  escrever(pdf, 'Rede', xRede + 3.2, yBus - 3.6, { size: 6.5, align: 'left' })
  escrever(pdf, 'de', xRede + 3.2, yBus, { size: 6.5, align: 'left' })
  escrever(pdf, 'Distribuição', xRede + 3.2, yBus + 3.6, { size: 6.5, align: 'left', max: 30 })

  desenharCarimbo(pdf, dados, resumo)
}

function linhasInversoresBlocos(inversores) {
  const linhas = []
  inversores.forEach((inv, indice) => {
    if (indice) linhas.push({ texto: ' ', size: 3.2 })
    const kw = num(inv.potenciaKw)
    linhas.push(
      { texto: kw > 0 ? `INVERSOR ${fmtKw(kw)} kW` : 'INVERSOR', size: 7, bold: true },
      { texto: String(inv.modelo || '').trim() || 'Modelo a informar', size: 6.5 },
      { texto: `V = ${tensaoCa(inv.ligacao)}`, size: 6.5 },
      { texto: inv.correnteA ? `In = ${inv.correnteA} A` : 'In a informar', size: 6.5 },
      { texto: '>> ANTI-ILHAMENTO', size: 6 },
      { texto: '>> AFCI', size: 6 },
      { texto: 'Qtd = 1', size: 6.5 }
    )
  })
  return linhas
}

export function montarDiagramaBlocos(dados = {}) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const resumo = resumoModulos(dados)
  const inversores = prepararInversores(dados)
  const mods = modulosDaFolha(dados)
  if (mods.length > 1 || inversores.length > 1) {
    desenharDiagramaReplicado(pdf, dados, mods, inversores, resumo)
    return pdf
  }
  const lado = 24
  const passo = 3.4
  const geracao = linhasGeracaoBlocos(dados)
  const textosInversor = linhasInversoresBlocos(inversores)
  const protecao = itensProtecao(dados, inversores)
  const alturaLinhas = (qtd) => (qtd > 0 ? (qtd - 1) * passo + 2.6 : 0)
  const alturaGrupo = 7.5 + lado + 9 + Math.max(
    alturaLinhas(geracao.length),
    alturaLinhas(textosInversor.length),
    alturaLinhas(protecao.length)
  )
  const yLivreTopo = 16
  const yLivreBase = 224
  const ySim = yLivreTopo + Math.max(0, (yLivreBase - yLivreTopo - alturaGrupo) / 2) + 7.5
  const yMeio = ySim + lado / 2
  const yTexto = ySim + lado + 9

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(6.5)
  const larguraRede = Math.max(pdf.getTextWidth('Rede de'), pdf.getTextWidth('Distribuição'))
  const origemX = 20
  const origemMed = 164
  const origemBus = 182
  const fimConteudo = origemBus + 3 + larguraRede
  const dx = (8 + 202) / 2 - (origemX + fimConteudo) / 2
  const geracaoX = origemX + dx
  const inversorX = 68 + dx
  const caX = 116 + dx
  const xMed = origemMed + dx
  const xBus = origemBus + dx

  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.7)
  pdf.rect(8, 8, 194, 281, 'S')

  const titulos = [
    ['Geração', geracaoX + lado / 2],
    ['Inversores', inversorX + lado / 2],
    ['Proteção CA', caX + lado / 2],
    ['Medição', xMed]
  ]
  titulos.forEach(([titulo, x]) => {
    escrever(pdf, titulo, x, ySim - 6, { size: 8, align: 'center' })
  })

  const ligar = (x1, x2) => traco(pdf, x1, yMeio, x2, yMeio, PRETO, 0.45)
  ligar(geracaoX + lado, inversorX)
  ligar(inversorX + lado, caX)
  ligar(caX + lado, xMed - 9)
  ligar(xMed + 9, xBus)
  traco(pdf, xBus, ySim + 2, xBus, ySim + lado - 2, PRETO, 0.7)

  simboloString(pdf, geracaoX, ySim, lado, lado)
  simboloInversor(pdf, inversorX, ySim, lado, lado)
  pdf.setDrawColor(0, 0, 0)
  pdf.setLineWidth(0.45)
  pdf.rect(caX, ySim, lado, lado, 'S')
  escrever(pdf, 'CA', caX + lado / 2, yMeio, { size: 12, bold: true, align: 'center' })
  pdf.circle(xMed, yMeio, 9, 'S')
  escrever(pdf, 'M', xMed, yMeio, { size: 12, bold: true, align: 'center' })
  escrever(pdf, 'Rede de', xBus + 3, yMeio - 1.8, { size: 6.5, align: 'left', max: 20 })
  escrever(pdf, 'Distribuição', xBus + 3, yMeio + 1.8, { size: 6.5, align: 'left', max: 20 })

  setaParaBaixo(pdf, geracaoX + lado / 2, yTexto - 1.4)
  setaParaBaixo(pdf, inversorX + lado / 2, yTexto - 1.4)
  setaParaBaixo(pdf, caX + lado / 2, yTexto - 1.4)

  const baseTexto = 220
  const larguraColuna = 40
  encaixarTexto(pdf, geracao, geracaoX + lado / 2, larguraColuna, yTexto, baseTexto, PRETO, 'topo')
  encaixarTexto(pdf, textosInversor, inversorX + lado / 2, larguraColuna, yTexto, baseTexto, PRETO, 'topo')
  encaixarTexto(pdf, protecao, caX + lado / 2, larguraColuna, yTexto, baseTexto, PRETO, 'topo')

  desenharCarimbo(pdf, dados, resumo)
  return pdf
}
